import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/axios';
import CustomerLayout from '../../components/Layout/CustomerLayout';
import { useLanguage } from '../../context/LanguageContext';
import { FaDownload, FaFileExcel, FaFilePdf, FaFilter, FaSearch, FaCalendarAlt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CustomerReportsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    contract_id: '',
    product_id: '',
    sla_group_id: '',
    sla_definition_id: '',
    start_date: '',
    end_date: '',
    user_id: ''
  });
  
  // Dropdown data
  const [contracts, setContracts] = useState([]);
  const [products, setProducts] = useState([]);
  const [slaGroups, setSlaGroups] = useState([]);
  const [slaDefinitions, setSlaDefinitions] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Report data
  const [reportData, setReportData] = useState([]);
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Validation popup state
  const [showValidationPopup, setShowValidationPopup] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.customer_id) {
        fetchContracts(userData.customer_id);
        fetchCustomerUsers(userData.customer_id);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Kontratları getir
  const fetchContracts = async (customerId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/customer-contracts', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { customer_id: customerId }
      });
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Ürünleri getir
  const fetchProducts = async (contractId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/contract-products', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { contract_id: contractId }
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // SLA gruplarını getir
  const fetchSlaGroups = async (productId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/contract-products', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { contract_id: filters.contract_id }
      });
      
      const selectedProduct = response.data.find(p => p.id === parseInt(productId));
      if (selectedProduct && selectedProduct.sla_group_id) {
        const groupResponse = await api.get(`/sla/${selectedProduct.sla_group_id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (groupResponse.data.success) {
          setSlaGroups([{
            id: selectedProduct.sla_group_id,
            group_name: groupResponse.data.sla.sla_group_name
          }]);
        }
      } else {
        setSlaGroups([]);
      }
    } catch (error) {
      console.error('Error fetching SLA groups:', error);
      setSlaGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  // SLA tanımlarını getir
  const fetchSlaDefinitions = async (slaGroupId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/sla/${slaGroupId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success && response.data.sla.definitions) {
        setSlaDefinitions(response.data.sla.definitions);
      } else {
        setSlaDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching SLA definitions:', error);
      setSlaDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri kullanıcılarını getir
  const fetchCustomerUsers = async (customerId) => {
    try {
      const response = await api.get('/customer-users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { customer_id: customerId }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching customer users:', error);
      setUsers([]);
    }
  };

  // Tüm kontratların ürünlerini getir ("Tümü" seçimi için)
  const fetchAllProducts = async () => {
    try {
      setIsLoading(true);
      const allProducts = [];
      
      for (const contract of contracts) {
        const response = await api.get('/contract-products', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { contract_id: contract.id }
        });
        allProducts.push(...(response.data || []));
      }
      
      // Tekrarlanan ürünleri temizle
      const uniqueProducts = allProducts.filter((product, index, self) => 
        index === self.findIndex(p => p.id === product.id)
      );
      
      setProducts(uniqueProducts);
    } catch (error) {
      console.error('Error fetching all products:', error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Tüm SLA gruplarını getir ("Tümü" seçimi için)
  const fetchAllSlaGroups = async () => {
    try {
      setIsLoading(true);
      const allSlaGroups = [];
      
      for (const product of products) {
        if (product.sla_group_id) {
          const groupResponse = await api.get(`/sla/${product.sla_group_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (groupResponse.data.success) {
            const existingGroup = allSlaGroups.find(g => g.id === product.sla_group_id);
            if (!existingGroup) {
              allSlaGroups.push({
                id: product.sla_group_id,
                group_name: groupResponse.data.sla.sla_group_name
              });
            }
          }
        }
      }
      
      setSlaGroups(allSlaGroups);
    } catch (error) {
      console.error('Error fetching all SLA groups:', error);
      setSlaGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Tüm SLA tanımlarını getir ("Tümü" seçimi için)
  const fetchAllSlaDefinitions = async () => {
    try {
      setIsLoading(true);
      const allSlaDefinitions = [];
      
      for (const group of slaGroups) {
        const response = await api.get(`/sla/${group.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.data.success && response.data.sla.definitions) {
          allSlaDefinitions.push(...response.data.sla.definitions);
        }
      }
      
      // Tekrarlanan tanımları temizle
      const uniqueDefinitions = allSlaDefinitions.filter((definition, index, self) => 
        index === self.findIndex(d => d.id === definition.id)
      );
      
      setSlaDefinitions(uniqueDefinitions);
    } catch (error) {
      console.error('Error fetching all SLA definitions:', error);
      setSlaDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter değişikliklerini handle et
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // Cascading dropdown logic - "Tümü" seçildiğinde de alt dropdown'ları doldur
    if (field === 'contract_id') {
      if (value && value !== '') {
        if (value === 'all') {
          // Tümü seçildiğinde tüm kontratların ürünlerini getir
          fetchAllProducts();
        } else {
          // Belirli kontrat seçildiğinde sadece o kontratın ürünlerini getir
          fetchProducts(value);
        }
      } else {
        setProducts([]);
      }
      setFilters(prev => ({ 
        ...prev, 
        product_id: '', 
        sla_group_id: '', 
        sla_definition_id: '' 
      }));
      setSlaGroups([]);
      setSlaDefinitions([]);
    } else if (field === 'product_id') {
      if (value && value !== '') {
        if (value === 'all') {
          // Tümü seçildiğinde tüm SLA gruplarını getir
          fetchAllSlaGroups();
        } else {
          // Belirli ürün seçildiğinde sadece o ürünün SLA gruplarını getir
          fetchSlaGroups(value);
        }
      } else {
        setSlaGroups([]);
      }
      setFilters(prev => ({ 
        ...prev, 
        sla_group_id: '', 
        sla_definition_id: '' 
      }));
      setSlaDefinitions([]);
    } else if (field === 'sla_group_id') {
      if (value && value !== '') {
        if (value === 'all') {
          // Tümü seçildiğinde tüm SLA tanımlarını getir
          fetchAllSlaDefinitions();
        } else {
          // Belirli SLA grup seçildiğinde sadece o grubun tanımlarını getir
          fetchSlaDefinitions(value);
        }
      } else {
        setSlaDefinitions([]);
      }
      setFilters(prev => ({ 
        ...prev, 
        sla_definition_id: '' 
      }));
    }
  };

  // Rapor oluştur
  const generateReport = async () => {
    // En azından bir filtre kriteri seçilmiş olmalı
    const hasFilter = 
      filters.contract_id || 
      filters.product_id || 
      filters.sla_group_id || 
      filters.sla_definition_id || 
      (filters.start_date && filters.end_date) || 
      filters.user_id;

    if (!hasFilter) {
      setShowValidationPopup(true);
      return;
    }

    try {
      setIsLoading(true);
      
      // API'den support case'leri getir
      const response = await api.get('/support-cases', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let filteredData = response.data.filter(caseItem => {
        // Müşteri filtresi - her zaman uygula
        const customerMatches = String(caseItem.customer_id) === String(user.customer_id);
        if (!customerMatches) return false;
        
        // Kontrat filtresi - sadece belirli kontrat seçilmişse uygula
        if (filters.contract_id && filters.contract_id !== 'all') {
          // Support case'den contract bilgisini almak için ek kontrol gerekebilir
          // Şimdilik bu filtreyi atla (API'de contract_id bilgisi support_case tablosunda yok)
        }
        
        // Tarih filtresi
        if (filters.start_date && filters.end_date) {
          const caseDate = new Date(caseItem.created_at);
          const startDate = new Date(filters.start_date);
          const endDate = new Date(filters.end_date);
          
          // EndDate'e 1 gün ekle ki o günün sonuna kadar olan veriler dahil olsun
          endDate.setDate(endDate.getDate() + 1);
          
          if (caseDate < startDate || caseDate >= endDate) return false;
        }
        
        // Kullanıcı filtresi - sadece belirli kullanıcı seçilmişse uygula
        if (filters.user_id && filters.user_id !== 'all') {
          if (String(caseItem.created_by) !== String(filters.user_id)) return false;
        }
        
        return true;
      });
      setReportData(filteredData);
      setReportGenerated(true);
      setCurrentPage(1); // Yeni rapor oluşturulduğunda ilk sayfaya dön
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(t('customerReports.reportGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('customerReports.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel
    const excelData = reportData.map((item, index) => ({
      'Sira No': index + 1,
      'Talep ID': cleanTextForExcel(item.case_id),
      'Konu': cleanTextForExcel(item.subject),
      'Durum': cleanTextForExcel(item.case_status),
      'Olusturma Tarihi': cleanTextForExcel(new Date(item.created_at).toLocaleDateString('tr-TR')),
      'Olusturan': cleanTextForExcel(item.created_by_name),
      'Kaynak': cleanTextForExcel(item.source),
      'Atanan': cleanTextForExcel(item.assigned_to_name || 'Atanmadi'),
      'Musteri': cleanTextForExcel(item.customer_name)
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 8 },  // Sira No
      { wch: 20 }, // Talep ID
      { wch: 30 }, // Konu
      { wch: 15 }, // Durum
      { wch: 15 }, // Olusturma Tarihi
      { wch: 20 }, // Olusturan
      { wch: 12 }, // Kaynak
      { wch: 20 }, // Atanan
      { wch: 25 }  // Musteri
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Destek Talepleri Raporu');

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    const filename = `Destek_Talepleri_Raporu_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // PDF export  
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('customerReports.noDataToExport'));
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Set font to support Turkish characters
    doc.setFont('helvetica', 'normal');

    // Helper function to handle Turkish characters
    const cleanText = (text) => {
      if (!text) return '';
      
      // Convert Turkish characters to Latin equivalents for PDF compatibility
      const turkishToLatin = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'I': 'I',
        'İ': 'I', 'i': 'i',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
      };
      
      let cleanedText = text.toString().replace(/undefined/g, '');
      
      // Replace Turkish characters
      for (const [turkish, latin] of Object.entries(turkishToLatin)) {
        cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
      }
      
      return cleanedText;
    };

    // Add title
    doc.setFontSize(16);
    doc.text('Destek Talepleri Raporu', 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, 14, 30);
    
    // Add user info
    if (user) {
      const customerName = cleanText(user.customer_name || user.full_name || 'N/A');
      const createdBy = cleanText(user.full_name || 'N/A');
      
      doc.text(`Musteri: ${customerName}`, 14, 36);
      doc.text(`Olusturan: ${createdBy}`, 14, 42);
    }

    // Prepare table data with clean text
    const tableData = reportData.map((item, index) => [
      index + 1,
      cleanText(item.case_id) || '',
      cleanText((item.subject || '').substring(0, 25) + (item.subject?.length > 25 ? '...' : '')),
      cleanText(item.case_status) || '',
      cleanText(new Date(item.created_at).toLocaleDateString('tr-TR')),
      cleanText((item.created_by_name || '').substring(0, 15) + (item.created_by_name?.length > 15 ? '...' : '')),
      cleanText(item.source) || '',
      cleanText((item.assigned_to_name || 'Atanmadi').substring(0, 15) + (item.assigned_to_name?.length > 15 ? '...' : ''))
    ]);

    // Add table
    autoTable(doc, {
      head: [['#', 'Talep ID', 'Konu', 'Durum', 'Tarih', 'Olusturan', 'Kaynak', 'Atanan']],
      body: tableData,
      startY: 48,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        font: 'helvetica',
        fontStyle: 'normal'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        font: 'helvetica'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 15 }, // #
        1: { cellWidth: 35 }, // Talep ID
        2: { cellWidth: 50 }, // Konu
        3: { cellWidth: 25 }, // Durum
        4: { cellWidth: 25 }, // Tarih
        5: { cellWidth: 35 }, // Olusturan
        6: { cellWidth: 20 }, // Kaynak
        7: { cellWidth: 35 }  // Atanan
      }
    });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    const filename = `Destek_Talepleri_Raporu_${currentDate}.pdf`;

    // Save file
    doc.save(filename);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      contract_id: '',
      product_id: '',
      sla_group_id: '',
      sla_definition_id: '',
      start_date: '',
      end_date: '',
      user_id: ''
    });
    setProducts([]);
    setSlaGroups([]);
    setSlaDefinitions([]);
    setReportData([]);
    setReportGenerated(false);
    setCurrentPage(1);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  return (
    <CustomerLayout>
      <main className="container mx-auto py-8 px-4 dark:text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">{t('customerReports.title')}</h1>
        </div>

                {/* Filter Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <FaFilter className="text-blue-500" size={16} />
            <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 dark:text-white">{t('customerReports.reportFilters')}</h2>
              </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-3 sm:mb-4">
                        {/* Kontrat Seçimi */}
              <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.contract')}</label>
              <select
                value={filters.contract_id}
                onChange={(e) => handleFilterChange('contract_id', e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              >
                <option value="">{t('customerReports.selectContract')}</option>
                <option value="all">{t('customerReports.all')}</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contract_name}
                  </option>
                ))}
              </select>
              </div>

                        {/* Ürün Seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.product')}</label>
              <select
                value={filters.product_id}
                onChange={(e) => handleFilterChange('product_id', e.target.value)}
                                 disabled={!filters.contract_id}
                 className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerReports.selectProduct')}</option>
                <option value="all">{t('customerReports.all')}</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>

            {/* SLA Group Seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.slaGroup')}</label>
              <select
                value={filters.sla_group_id}
                onChange={(e) => handleFilterChange('sla_group_id', e.target.value)}
                                 disabled={!filters.product_id}
                 className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerReports.selectSlaGroup')}</option>
                <option value="all">{t('customerReports.all')}</option>
                {slaGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
          </div>

            {/* SLA Definition Seçimi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.slaDefinition')}</label>
              <select
                value={filters.sla_definition_id}
                onChange={(e) => handleFilterChange('sla_definition_id', e.target.value)}
                                 disabled={!filters.sla_group_id}
                 className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerReports.selectSlaDefinition')}</option>
                <option value="all">{t('customerReports.all')}</option>
                {slaDefinitions.map(definition => (
                  <option key={definition.id} value={definition.id}>
                    {definition.sla_severity}
                  </option>
                ))}
              </select>
              </div>

            {/* Başlangıç Tarihi */}
              <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.startDate')}</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              />
              </div>

            {/* Bitiş Tarihi */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.endDate')}</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              />
          </div>

            {/* Kullanıcı Seçimi */}
              <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerReports.user')}</label>
              <select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              >
                <option value="">{t('customerReports.selectUser')}</option>
                <option value="all">{t('customerReports.all')}</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
              </div>
              </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={generateReport}
              disabled={isLoading}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-xs sm:text-sm"
            >
              <FaSearch size={14} className="sm:hidden" />
              <FaSearch size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">{isLoading ? t('customerReports.generating') : t('customerReports.generateReport')}</span>
              <span className="sm:hidden">{isLoading ? 'Generating...' : 'Generate'}</span>
            </button>

                         <button
               onClick={clearFilters}
               className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs sm:text-sm"
            >
              <MdClose size={14} className="sm:hidden" />
              <MdClose size={16} className="hidden sm:block" />
              <span className="hidden sm:inline">{t('customerReports.clearFilters')}</span>
              <span className="sm:hidden">Clear</span>
            </button>

            {reportGenerated && (
              <>
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs sm:text-sm"
                >
                  <FaFileExcel size={14} className="sm:hidden" />
                  <FaFileExcel size={16} className="hidden sm:block" />
                  <span className="hidden sm:inline">{t('customerReports.downloadExcel')}</span>
                  <span className="sm:hidden">Excel</span>
                </button>

                <button
                  onClick={exportToPDF}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs sm:text-sm"
                >
                  <FaFilePdf size={14} className="sm:hidden" />
                  <FaFilePdf size={16} className="hidden sm:block" />
                  <span className="hidden sm:inline">{t('customerReports.downloadPdf')}</span>
                  <span className="sm:hidden">PDF</span>
                </button>
              </>
            )}
              </div>
            </div>

                {/* Report Results */}
        {reportGenerated && (
                     <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Sayfa boyutu seçici */}
                         <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 rounded-tl-lg rounded-tr-lg">
               <div className="flex items-center gap-1 sm:gap-2">
                 <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{t('customerReports.show')}</span>
                 <select
                   value={itemsPerPage}
                   onChange={(e) => handlePageSizeChange(e.target.value)}
                   className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-1.5 sm:px-2 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                                 <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{t('customerReports.records')}</span>
               </div>
               <div className="flex items-center gap-2 sm:gap-4">
              
                 <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                   {t('customerReports.totalRecordsFound', { count: reportData.length })}
                 </span>
          </div>
        </div>

            {reportData.length > 0 ? (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block">
                  <table className="w-full table-auto">
                                         <thead className="bg-gray-50 dark:bg-gray-700">
                       <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.caseId')}</th>
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.subject')}</th>
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.status')}</th>
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.creationDate')}</th>
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.createdBy')}</th>
                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerReports.source')}</th>
                      </tr>
                    </thead>
                                         <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                       {currentData.map((item, index) => (
                         <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                           <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.case_id}</td>
                           <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            <span className="relative group">
                              {item.subject.length > 30 
                                ? item.subject.substring(0, 30) + '...' 
                                : item.subject}
                                                             {item.subject.length > 30 && (
                                 <div className="absolute z-10 invisible group-hover:visible bg-gray-800 dark:bg-gray-600 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 min-w-max max-w-xs whitespace-normal break-words shadow-lg">
                                   {item.subject}
              </div>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                              item.case_status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                              item.case_status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                              item.case_status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.case_status}
                            </span>
                          </td>
                                                     <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                             {new Date(item.created_at).toLocaleDateString('tr-TR')}
                           </td>
                           <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.created_by_name || '-'}</td>
                           <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.source || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View - Single Column */}
                <div className="lg:hidden p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {currentData.map((item, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.caseId')}</span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">{item.case_id}</span>
                          </div>
                          
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.subject')}</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 line-clamp-2">
                              {item.subject.length > 40 
                                ? item.subject.substring(0, 40) + '...' 
                                : item.subject}
                            </p>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.status')}</span>
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                              item.case_status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                              item.case_status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                              item.case_status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.case_status}
                            </span>
                          </div>
                          
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.creationDate')}</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              {new Date(item.created_at).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                          
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.createdBy')}</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              {item.created_by_name || '-'}
                            </p>
                          </div>
                          
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('customerReports.source')}</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              {item.source || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
                             <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                 <p>{t('customerReports.noDataFound')}</p>
               </div>
            )}
          </div>
        )}

                {/* Sayfalama */}
                {totalPages > 1 && (
                                     <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                     <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between mb-6">
                       <div>
                         <p className="text-sm text-gray-700 dark:text-gray-300">
                           {t('customerReports.showing')}{' '}
                           <span className="font-medium">{indexOfFirstItem + 1}</span>
                           {' '}-{' '}
                           <span className="font-medium">
                             {Math.min(indexOfLastItem, reportData.length)}
                           </span>{' '}
                           / {t('customerReports.total')}{' '}
                           <span className="font-medium">{reportData.length}</span>{' '}
                           {t('customerReports.records')}
          </p>
        </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                                                         className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                               currentPage === 1
                                 ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                 : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                             }`}
                          >
                            {t('customerReports.previous')}
                          </button>
                          {[...Array(totalPages)].map((_, index) => (
                            <button
                              key={index + 1}
                              onClick={() => handlePageChange(index + 1)}
                                                             className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                                 currentPage === index + 1
                                   ? 'bg-blue-600 text-white'
                                   : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                               }`}
                            >
                              {index + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                                                         className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                               currentPage === totalPages
                                 ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                 : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                             }`}
                          >
                            {t('customerReports.next')}
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}

        {/* Validation Popup */}
        {showValidationPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
              <div className="bg-red-500 text-white p-4 rounded-t-lg flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  {t('customerReports.filterRequired')}
                </h3>
                <button 
                  onClick={() => setShowValidationPopup(false)}
                  className="text-white hover:text-red-200"
                >
                  <MdClose size={20} />
                </button>
        </div>

                             <div className="p-6">
                 <p className="text-gray-700 dark:text-gray-300 mb-4">
                   {t('customerReports.filterRequiredMessage')}
                 </p>
                 
                 <div className="flex justify-end gap-3">
                   <button
                     onClick={() => setShowValidationPopup(false)}
                     className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    {t('customerReports.understood')}
                  </button>
                </div>
              </div>
            </div>
        </div>
        )}
      </main>
    </CustomerLayout>
  );
};

export default CustomerReportsPage;